/**
 * Role capabilities for the multi-organisation read-only roles.
 *
 * Two roles browse several organisations but must not change anything:
 *  - multi_org_viewer — sees everything in its granted orgs, may only comment.
 *  - finance_admin    — the same, plus full edit rights inside billing.
 *
 * The backend already refuses their writes (default-deny guard), so nothing here
 * is a security boundary — it exists so these users are never shown a control
 * that would 403. Read-only users staring at Save buttons that fail is the bug
 * this module prevents.
 *
 * Everything is written as an allow-list of the *new* roles: every pre-existing
 * role falls through to "may edit", so current behaviour is untouched.
 *
 * Routing-wise both roles behave exactly like multi_org_admin — they have no
 * single home org and browse via /:orgSlug/* or /all — see orgPath.ts.
 */

import type { User } from '../types';

type Role = User['role'] | string | undefined;

/** The roles that may look at everything but only write comments. */
const READ_ONLY_ROLES = new Set(['multi_org_viewer', 'finance_admin']);

/** Roles whose org context comes from the URL/org switcher rather than user.organization_id. */
const MULTI_ORG_ROLES = new Set(['multi_org_admin', 'multi_org_viewer', 'finance_admin']);

/**
 * True for the multi-org read-only roles (multi_org_viewer, finance_admin).
 * Use it to hide create/destroy controls; unknown/undefined roles are treated as
 * "not read-only" so nothing changes for the existing roles.
 */
export function isReadOnlyRole(role: Role): boolean {
  return !!role && READ_ONLY_ROLES.has(role);
}

/**
 * Inverse of isReadOnlyRole, for the common `{canEditContent(role) && <Save/>}`
 * shape — reads better in JSX than a negated check.
 */
export function canEditContent(role: Role): boolean {
  return !isReadOnlyRole(role);
}

/**
 * Billing writes (expenses, invoice fields, the ELO link, approval toggles).
 * finance_admin exists precisely for this, so it gets through; multi_org_viewer
 * does not. Exports are GETs and are never gated on this.
 */
export function canEditBilling(role: Role): boolean {
  return role === 'finance_admin' || canEditContent(role);
}

/**
 * Commenting is the one write both new roles keep, so this is deliberately true
 * for everyone. It exists so comment buttons are gated by their own predicate
 * and can never be swept up by a generic "can edit" check.
 */
export function canComment(_role: Role): boolean {
  return true;
}

/** True for roles that browse many orgs (multi_org_admin and the two new roles). */
export function isMultiOrgRole(role: Role): boolean {
  return !!role && MULTI_ORG_ROLES.has(role);
}

/**
 * True for the role whose whole reason to exist is billing, so billing — not the
 * cross-org dashboard — is its landing page and gets a top-level nav entry.
 *
 * Only finance_admin: multi_org_admin and multi_org_viewer keep the dashboard home.
 * This is presentation only; every other section stays reachable, because
 * finance_admin reads everywhere (see the module header).
 */
export function landsOnBilling(role: Role): boolean {
  return role === 'finance_admin';
}

/**
 * Where a multi-org role goes when it lands on the /all shell with no path of its own:
 * after login, from the shell's logo, and from the shell's catch-all route.
 * One function so those three cannot disagree about a role's home.
 */
export function multiOrgHomePath(role: Role): string {
  return landsOnBilling(role) ? '/all/billing' : '/all';
}

/**
 * Roles that may reach a user-management screen at all.
 *
 * Mirrors the backend allowlist: `adminRouterGuard` (cabos_backend
 * src/middleware/adminAccessGuard.ts) lets `admin`/`super_admin` through the whole
 * /api/admin router and `multi_org_admin` through /api/admin/users* only. Every other
 * role — including multi_org_viewer and finance_admin — is refused there, and
 * `organization_admin` has its own /api/organizations/users* endpoints.
 *
 * multi_org_viewer and finance_admin are deliberately absent: they are read-only outside
 * comments/billing, so a Users link would lead straight to a 403.
 */
const USER_MANAGEMENT_ROLES = new Set([
  'admin', 'super_admin', 'organization_admin', 'multi_org_admin',
]);

/** True for roles that get a Users section (nav entry AND route). */
export function canManageUsers(role: Role): boolean {
  return !!role && USER_MANAGEMENT_ROLES.has(role);
}

/**
 * True for roles that may run the PSS / Mediaslide imports.
 *
 * Hidden entirely from the read-only roles on the client's instruction ("the viewer needs to hide
 * the import completely"). Applied to `finance_admin` as well as `multi_org_viewer`, not just the
 * one role named: an import is a bulk WRITE, every import endpoint is refused for both roles by the
 * backend's default-deny write guard, and `finance_admin`'s one exception is `/api/billing` — which
 * no import route is under. So for either role the entire section is seven screens that can only
 * end in a 403, and the PSS Finance Import is the one most likely to look like it belongs to a
 * finance role while being exactly as refused as the rest.
 *
 * Written as its own predicate rather than reusing `canEditContent` so that a future "let finance
 * run the finance import" stays a one-line change here, instead of having to be untangled from
 * every other edit permission.
 */
export function canRunImports(role: Role): boolean {
  return canEditContent(role);
}

/**
 * True for the ONE role that may bulk-create users — POST /api/admin/users/bulk.
 *
 * `super_admin` and nothing else, and it must stay a set of one. The backend handler checks
 * `realRole(req) === 'super_admin'` and 403s everyone else, INCLUDING `admin`: `adminRouterGuard`
 * grants `admin` the whole /api/admin router (FULL_ADMIN_ROLES), so the endpoint's own check is what
 * stops it, and this predicate has to agree or an `admin` gets a screen that 403s on submit.
 *
 * `admin` is the reason this is not `canReadAdminDirectory` or `canDeleteUsers` (both of which
 * include it) and the reason it is not written inline at the route: /admin/* is mounted for
 * `['super_admin', 'admin']` in App.tsx, so an `admin` reaches the super-admin shell and would
 * otherwise reach this route by typing the URL. It gates the ROUTE as well as the link.
 *
 * One endpoint creating dozens of accounts and mailing dozens of outsiders is the most consequential
 * button in the application; widening this predicate means widening the endpoint's own check first.
 */
export function canBulkCreateUsers(role: Role): boolean {
  return role === 'super_admin';
}

/**
 * True for the ONE role that may configure the nightly Google Sheets billing export — every
 * endpoint under /api/admin/sheets-export.
 *
 * `super_admin` and nothing else, and it must stay a set of one. Every handler in
 * cabos_backend src/routes/adminSheetsExport.ts calls `requireSuperAdmin` and 403s everyone else
 * INCLUDING `admin` — `adminRouterGuard` grants `admin` the whole /api/admin router
 * (FULL_ADMIN_ROLES), so the per-handler check is what stops it, exactly as for
 * POST /api/admin/users/bulk. This predicate has to agree, or an `admin` gets a screen that 403s
 * on load and on every button.
 *
 * `admin` is also the reason this gates the ROUTE and not only the link: /admin/* is mounted for
 * `['super_admin', 'admin']` in App.tsx, so an `admin` reaches the super-admin shell and would
 * otherwise get to the screen by typing the URL.
 *
 * Written as its own predicate rather than reusing `canBulkCreateUsers` — identical today — because
 * the two answer different questions, and because what this one guards is a stored credential that
 * can WRITE to the client's finance spreadsheets. Widening it means widening the endpoints' own
 * checks first.
 */
export function canConfigureSheetsExport(role: Role): boolean {
  return role === 'super_admin';
}

/**
 * True for roles that may read the global model/agency directory — GET /api/admin/models
 * and GET /api/admin/agencies.
 *
 * `adminRouterGuard` allows the whole /api/admin router for `admin`/`super_admin` only;
 * `multi_org_admin` is allowlisted for /api/admin/users* alone, and the read-only multi-org
 * roles are refused everywhere under it. So every other role gets a guaranteed 403 from
 * those two endpoints, and the pickers they feed must simply not be fetched — a 403 on
 * every visit is console noise that hides real errors.
 *
 * The org-scoped equivalents (/api/organizations/models, /api/organizations/agencies) are
 * a different question and are NOT gated on this.
 */
export function canReadAdminDirectory(role: Role): boolean {
  return role === 'admin' || role === 'super_admin';
}

/**
 * True for roles that may write `UserOrganizationAccess` rows, i.e. call
 * PUT /api/admin/users/:id/organizations. That endpoint is on the multi-org admin
 * allowlist; an `organization_admin` cannot reach /api/admin at all, so it must never be
 * offered a role whose only source of access is that table.
 */
export function canManageOrgAccess(role: Role): boolean {
  return role === 'admin' || role === 'super_admin' || role === 'multi_org_admin';
}

/**
 * True for roles that may assign `admin` / `super_admin`. `assertAssignableRole` in the
 * backend guard refuses those two for a multi_org_admin, so the options are hidden rather
 * than shown and then rejected on save.
 */
export function canAssignAdminRoles(role: Role): boolean {
  return role === 'admin' || role === 'super_admin';
}

/**
 * True for roles that may deactivate a user or reset someone's password.
 * DELETE /api/admin/users/:id and POST /api/admin/users/:id/reset-password are left OFF
 * the multi-org admin allowlist on purpose (see MULTI_ORG_ADMIN_ROUTES), so those controls
 * are hidden for it instead of 403-ing.
 */
export function canDeleteUsers(role: Role): boolean {
  return role === 'admin' || role === 'super_admin';
}

/**
 * True for roles that may re-credential an existing account — POST
 * /api/admin/users/:id/send-credentials, which sets a fresh password and mails the person the same
 * welcome email a newly created user gets.
 *
 * The same two roles as `canDeleteUsers`, and for the same reason: the endpoint is left OFF the
 * multi-org admin allowlist (see MULTI_ORG_ADMIN_ROUTES) because setting a password on somebody
 * else's account is the account-takeover route, and its handler checks `admin`/`super_admin`
 * independently on top of that. So the control is hidden rather than shown and 403'd.
 *
 * Written as its own predicate rather than reusing `canDeleteUsers` even though the two are
 * identical today: they answer different questions ("may deactivate" vs "may mail a new password"),
 * and the plausible next change — letting a multi_org_admin re-send credentials for the users it
 * already manages, without also letting it deactivate them — has to be a one-line edit here rather
 * than an untangling of every call site of a shared predicate.
 */
export function canSendCredentials(role: Role): boolean {
  // `multi_org_admin` added on the client's instruction — it creates the accounts and is who people
  // ask when a welcome mail never arrived. It is NOT unbounded: the backend puts every target
  // through `loadManageableTarget`, so the reachable set is the accounts this role could already
  // rename or re-role, never an admin and never an agency login. Note this deliberately does not
  // widen `canDeleteUsers`, which is the separation those two predicates exist for.
  return role === 'admin' || role === 'super_admin' || role === 'multi_org_admin';
}

/** Tooltip for inline fields we disable rather than hide, so the reason is visible. */
export const READ_ONLY_HINT = 'Read-only: your role cannot change this';
